import { useCallback, useEffect, useRef, useState } from 'react'
import { WorkflowTemplate } from '@digital-twins/shared-types'

/**
 * Composer armed-workflow snapshot for one thread tab.
 *
 * sessionStorage, keyed on the thread token: survives F5 / HMR inside the
 * same tab, dies with the tab. Audience ids are stored as strings and are
 * NOT resolved on read — a 500-row audience fetch on remount is the thing
 * `ThreadCommandMenu` already latches away from. Confirm time is when a
 * bad audience fails, same as the server.
 *
 * When a stored entry exists but fails validation, the key is removed and a
 * one-shot drop flag is written so the page can offer resume chrome. That
 * signal is storage-backed (not inferred from turn history — messages carry
 * no mode field). Cleared on dismiss, intentional clear, or a successful arm.
 *
 * Remembered audience is a separate concern with its own storage: localStorage,
 * namespaced per organization (`rememberedAudienceStorageKey(orgId)`), not
 * sessionStorage and not the thread. A pick made in one org must never
 * surface in another, and it must survive a closed tab the way a "remember
 * this" choice should. Written only on confirm (never on checkbox toggle),
 * and it only ever pre-fills a validated confirm-card field as a
 * convenience default — it never arms a command or skips the confirm
 * screen, which always renders and always requires an explicit click.
 * `clear()` drops the arm so the card can reopen; it does not forget the
 * audience — chip dismiss calls `forgetRememberedAudience` for that.
 * Audience ids are still not resolved on read.
 */
export interface ArmedWorkflowState {
  slug: string | null
  template: WorkflowTemplate | null
  inputs: Record<string, string | null>
  intakeDirection: string | null
  /**
   * Display name for the armed chip. Persisted with the snapshot so F5
   * remount still shows it when the remember-audience checkbox was off.
   * Not a fetch — just the label the confirm/skip path already knew.
   */
  audienceName: string | null
  /**
   * Last `(threadToken, direction, audience)` tuple that already attempted
   * auto-start. Survives remount so a failed start does not re-enter on the
   * same "direction + audience exist" predicate. Cleared on a new arm, Keep
   * talking (direction null), or `clear()`.
   */
  latchedStartKey: string | null
  /**
   * Carries the media/stimulus gate override across the off-thread hand-off:
   * a composer that already resolved "proceed without a stimulus" must not
   * ask again once the thread page starts the armed run. Optional so an
   * entry written before this field existed, or a caller that never sets it,
   * still type-checks — always read as `false` when absent.
   */
  proceedWithoutStimulus?: boolean
}

export interface RememberedAudience {
  audienceId: string
  audienceName: string
}

export interface ArmedWorkflowLoadResult {
  state: ArmedWorkflowState
  /**
   * True when a persisted armed entry was dropped on this read (failed
   * validation / bad JSON), or a prior drop flag is still pending dismiss.
   */
  dropped: boolean
}

export const EMPTY_ARMED_WORKFLOW_STATE: ArmedWorkflowState = {
  slug: null,
  template: null,
  inputs: {},
  intakeDirection: null,
  audienceName: null,
  latchedStartKey: null,
  proceedWithoutStimulus: false,
}

const ARMED_WORKFLOW_STORAGE_PREFIX = 'thread-armed-workflow:'
const ARMED_WORKFLOW_DROP_FLAG_PREFIX = 'thread-armed-workflow-dropped:'
const REMEMBERED_AUDIENCE_STORAGE_PREFIX = 'thread-remembered-audience:'
/**
 * Namespace for a remembered audience read/written before the current org
 * has resolved (`useCurrentOrg` is async; `orgId` starts `null`). Writes
 * only ever happen from a confirmed card, by which point the org has long
 * since loaded, so this bucket is expected to stay empty in practice — it
 * exists so a pre-hydration read cannot throw rather than to hold real data.
 */
const REMEMBERED_AUDIENCE_UNSCOPED_NAMESPACE = 'unscoped'

export function armedWorkflowStorageKey(threadToken: string): string {
  return `${ARMED_WORKFLOW_STORAGE_PREFIX}${threadToken}`
}

export function armedWorkflowDropFlagKey(threadToken: string): string {
  return `${ARMED_WORKFLOW_DROP_FLAG_PREFIX}${threadToken}`
}

export function rememberedAudienceStorageKey(orgId: string | null): string {
  return `${REMEMBERED_AUDIENCE_STORAGE_PREFIX}${orgId ?? REMEMBERED_AUDIENCE_UNSCOPED_NAMESPACE}`
}

/**
 * Latch identity for one auto-start attempt. JSON array so a direction that
 * contains separators cannot collide with another tuple.
 */
export function buildIntakeStartLatchKey(
  threadToken: string,
  direction: string,
  audience: string,
): string {
  return JSON.stringify([threadToken, direction, audience])
}

function getSessionStorage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * localStorage, not sessionStorage: the remembered audience must survive a
 * closed tab and must not follow the armed-workflow snapshot's tab lifetime.
 */
function getLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isInputValueRecord(
  value: unknown,
): value is Record<string, string | null> {
  if (!isRecord(value)) {
    return false
  }

  return Object.values(value).every(
    (entry) => entry === null || typeof entry === 'string',
  )
}

function isStoredInputSpec(value: unknown): boolean {
  if (!isRecord(value)) {
    return false
  }

  return (
    typeof value.key === 'string' &&
    typeof value.label === 'string' &&
    typeof value.type === 'string' &&
    typeof value.required === 'boolean'
  )
}

/**
 * Shape check only. Does not hit the network. A stored template that cannot
 * still stand in as `WorkflowTemplate` is treated as unresolvable and the
 * whole entry is dropped. `mdBody` may be missing or empty — the snapshot
 * strips it on write so intake-direction updates do not re-serialize ~11KB.
 */
function isStoredWorkflowTemplate(value: unknown): value is WorkflowTemplate {
  if (!isRecord(value)) {
    return false
  }

  if (value.mdBody != null && typeof value.mdBody !== 'string') {
    return false
  }

  return (
    typeof value.token === 'string' &&
    value.token.length > 0 &&
    typeof value.slug === 'string' &&
    value.slug.length > 0 &&
    typeof value.version === 'string' &&
    typeof value.displayName === 'string' &&
    typeof value.description === 'string' &&
    typeof value.mdHash === 'string' &&
    Array.isArray(value.inputs) &&
    value.inputs.every(isStoredInputSpec) &&
    Array.isArray(value.steps) &&
    typeof value.isSeeded === 'boolean' &&
    typeof value.isActive === 'boolean' &&
    typeof value.createdAt === 'string' &&
    typeof value.updatedAt === 'string'
  )
}

function slimTemplateForStorage(template: WorkflowTemplate): WorkflowTemplate {
  return { ...template, mdBody: '' }
}

function toPersistedArmedWorkflowState(
  state: ArmedWorkflowState,
): ArmedWorkflowState {
  if (!state.template) {
    return state
  }

  return {
    ...state,
    template: slimTemplateForStorage(state.template),
  }
}

function parseArmedWorkflowState(value: unknown): ArmedWorkflowState | null {
  if (!isRecord(value)) {
    return null
  }

  if (typeof value.slug !== 'string' || value.slug.length === 0) {
    return null
  }

  let template: WorkflowTemplate | null = null
  if (value.template != null) {
    if (!isStoredWorkflowTemplate(value.template)) {
      return null
    }
    if (value.template.slug !== value.slug) {
      return null
    }
    template = {
      ...value.template,
      mdBody: value.template.mdBody ?? '',
    }
  }

  if (!isInputValueRecord(value.inputs)) {
    return null
  }

  if (value.intakeDirection != null && typeof value.intakeDirection !== 'string') {
    return null
  }

  if (value.audienceName != null && typeof value.audienceName !== 'string') {
    return null
  }

  const latchedStartKey =
    typeof value.latchedStartKey === 'string' && value.latchedStartKey.length > 0
      ? value.latchedStartKey
      : null

  return {
    slug: value.slug,
    template,
    inputs: value.inputs,
    intakeDirection:
      typeof value.intakeDirection === 'string' ? value.intakeDirection : null,
    audienceName:
      typeof value.audienceName === 'string' && value.audienceName.length > 0
        ? value.audienceName
        : null,
    latchedStartKey,
    proceedWithoutStimulus: value.proceedWithoutStimulus === true,
  }
}

function hasArmedWorkflowDropFlag(threadToken: string): boolean {
  const storage = getSessionStorage()
  if (!storage || !threadToken) {
    return false
  }

  try {
    return storage.getItem(armedWorkflowDropFlagKey(threadToken)) === '1'
  } catch {
    return false
  }
}

function markArmedWorkflowDropped(threadToken: string) {
  const storage = getSessionStorage()
  if (!storage || !threadToken) {
    return
  }

  try {
    storage.setItem(armedWorkflowDropFlagKey(threadToken), '1')
  } catch {
    // Best-effort; the in-memory load result still carries `dropped: true`.
  }
}

export function clearArmedWorkflowDropFlag(threadToken: string) {
  const storage = getSessionStorage()
  if (!storage || !threadToken) {
    return
  }

  try {
    storage.removeItem(armedWorkflowDropFlagKey(threadToken))
  } catch {
    // Best-effort.
  }
}

/**
 * Read the armed snapshot and whether a persisted entry was dropped.
 *
 * A missing key is not a drop. Invalid JSON, missing slug, incomplete
 * template, or slug/template mismatch removes the key and sets the drop
 * flag so the page can show resume chrome.
 */
export function loadArmedWorkflowState(
  threadToken: string,
): ArmedWorkflowLoadResult {
  const storage = getSessionStorage()
  if (!storage || !threadToken) {
    return { state: EMPTY_ARMED_WORKFLOW_STATE, dropped: false }
  }

  try {
    const raw = storage.getItem(armedWorkflowStorageKey(threadToken))
    if (!raw) {
      return {
        state: EMPTY_ARMED_WORKFLOW_STATE,
        dropped: hasArmedWorkflowDropFlag(threadToken),
      }
    }

    const parsed = parseArmedWorkflowState(JSON.parse(raw))
    if (!parsed) {
      storage.removeItem(armedWorkflowStorageKey(threadToken))
      markArmedWorkflowDropped(threadToken)
      return { state: EMPTY_ARMED_WORKFLOW_STATE, dropped: true }
    }

    clearArmedWorkflowDropFlag(threadToken)
    return { state: parsed, dropped: false }
  } catch {
    try {
      storage.removeItem(armedWorkflowStorageKey(threadToken))
    } catch {
      // sessionStorage is best-effort; an unreadable entry is treated as empty.
    }
    markArmedWorkflowDropped(threadToken)
    return { state: EMPTY_ARMED_WORKFLOW_STATE, dropped: true }
  }
}

export function readArmedWorkflowState(
  threadToken: string,
): ArmedWorkflowState {
  return loadArmedWorkflowState(threadToken).state
}

export function writeArmedWorkflowState(
  threadToken: string,
  state: ArmedWorkflowState,
) {
  const storage = getSessionStorage()
  if (!storage || !threadToken) {
    return
  }

  const parsed = parseArmedWorkflowState(toPersistedArmedWorkflowState(state))
  if (!parsed) {
    try {
      storage.removeItem(armedWorkflowStorageKey(threadToken))
    } catch {
      // Best-effort; in-memory state still updates.
    }
    return
  }

  try {
    storage.setItem(armedWorkflowStorageKey(threadToken), JSON.stringify(parsed))
    clearArmedWorkflowDropFlag(threadToken)
  } catch {
    // Best-effort; the composer still holds the in-memory snapshot.
  }
}

export function clearArmedWorkflowStorage(threadToken: string) {
  const storage = getSessionStorage()
  if (!storage || !threadToken) {
    return
  }

  try {
    storage.removeItem(armedWorkflowStorageKey(threadToken))
  } catch {
    // Best-effort.
  }
  clearArmedWorkflowDropFlag(threadToken)
}

function parseRememberedAudience(value: unknown): RememberedAudience | null {
  if (!isRecord(value)) {
    return null
  }

  if (typeof value.audienceId !== 'string' || value.audienceId.length === 0) {
    return null
  }

  if (typeof value.audienceName !== 'string' || value.audienceName.length === 0) {
    return null
  }

  return {
    audienceId: value.audienceId,
    audienceName: value.audienceName,
  }
}

/**
 * Per-organization remembered audience id (and the name the chip reads).
 * localStorage, namespaced by `orgId` so a pick in one organization never
 * surfaces in another. Shape check only — the id is not resolved against
 * the 500-row list on read; the caller (`applyRememberedAudience`) must
 * validate it against the live audience list before treating it as filled.
 */
export function loadRememberedAudience(
  orgId: string | null,
): RememberedAudience | null {
  const storage = getLocalStorage()
  if (!storage) {
    return null
  }

  const key = rememberedAudienceStorageKey(orgId)
  try {
    const raw = storage.getItem(key)
    if (!raw) {
      return null
    }

    const parsed = parseRememberedAudience(JSON.parse(raw))
    if (!parsed) {
      storage.removeItem(key)
      return null
    }

    return parsed
  } catch {
    try {
      storage.removeItem(key)
    } catch {
      // localStorage is best-effort; an unreadable entry is treated as empty.
    }
    return null
  }
}

export function writeRememberedAudience(
  orgId: string | null,
  audience: RememberedAudience,
) {
  const storage = getLocalStorage()
  if (!storage) {
    return
  }

  const key = rememberedAudienceStorageKey(orgId)
  const parsed = parseRememberedAudience(audience)
  if (!parsed) {
    try {
      storage.removeItem(key)
    } catch {
      // Best-effort; in-memory state still updates on a valid write.
    }
    return
  }

  try {
    storage.setItem(key, JSON.stringify(parsed))
  } catch {
    // Best-effort; the composer still holds the in-memory snapshot.
  }
}

export function clearRememberedAudienceStorage(orgId: string | null) {
  const storage = getLocalStorage()
  if (!storage) {
    return
  }

  try {
    storage.removeItem(rememberedAudienceStorageKey(orgId))
  } catch {
    // Best-effort.
  }
}

function loadArmedOrEmpty(threadToken: string): ArmedWorkflowLoadResult {
  if (!threadToken) {
    return { state: EMPTY_ARMED_WORKFLOW_STATE, dropped: false }
  }

  return loadArmedWorkflowState(threadToken)
}

export function useArmedWorkflowState(
  threadToken: string,
  orgId: string | null,
) {
  const [initialLoad] = useState(() => loadArmedOrEmpty(threadToken))
  const [state, setState] = useState<ArmedWorkflowState>(initialLoad.state)
  /* Drop flag is storage-backed, so a later load after a bad entry was
     removed still reports `dropped: true` via the flag. */
  const [wasDropped, setWasDropped] = useState(initialLoad.dropped)
  const [rememberedAudience, setRememberedAudience] =
    useState<RememberedAudience | null>(() => loadRememberedAudience(orgId))
  const hydratedTokenRef = useRef(threadToken)
  /**
   * `useCurrentOrg` resolves asynchronously, so `orgId` is typically `null`
   * on first render — after the synchronous `rememberedAudience` initializer
   * above has already run against that `null` namespace. Mirrors
   * `hydratedTokenRef`: re-hydrate once the real org id lands, and again on
   * every later org switch, so a remembered audience never leaks across
   * organizations.
   */
  const hydratedOrgIdRef = useRef(orgId)
  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (hydratedTokenRef.current === threadToken) {
      return
    }
    hydratedTokenRef.current = threadToken

    if (!threadToken) {
      setState(EMPTY_ARMED_WORKFLOW_STATE)
      setWasDropped(false)
      return
    }

    const loaded = loadArmedWorkflowState(threadToken)
    setState(loaded.state)
    setWasDropped(loaded.dropped)
  }, [threadToken])

  useEffect(() => {
    /* C5 (plan 2026-09-14-twin-test-bound-audience): a transient `null` —
       `useCurrentOrg` briefly reporting no org while resolving a SWITCH from
       one real org to another, not the initial unresolved state — must not
       re-hydrate from the `unscoped` bucket. Skipping while `orgId` is null
       leaves `hydratedOrgIdRef` (and `rememberedAudience`) exactly where they
       were; the ref then still differs from whatever real org id lands next,
       so that transition re-hydrates correctly, and a bounce back to the SAME
       org this ref already reflects correctly skips a redundant reload. */
    if (orgId === null) {
      return
    }
    if (hydratedOrgIdRef.current === orgId) {
      return
    }
    hydratedOrgIdRef.current = orgId
    setRememberedAudience(loadRememberedAudience(orgId))
  }, [orgId])

  function persist(next: ArmedWorkflowState) {
    /* Keep stateRef in lockstep with the write so a later persist in the same
       turn (Keep talking clearing after latchIntakeStart) does not spread a
       stale snapshot and resurrect intakeDirection. */
    stateRef.current = next
    setState(next)
    if (!threadToken) {
      return
    }
    if (next.slug) {
      writeArmedWorkflowState(threadToken, next)
      setWasDropped(false)
    } else {
      clearArmedWorkflowStorage(threadToken)
      setWasDropped(false)
    }
  }

  function armSlug(slug: string) {
    persist({
      slug,
      template: null,
      inputs: {},
      intakeDirection: null,
      audienceName: null,
      latchedStartKey: null,
    })
  }

  function clear() {
    persist(EMPTY_ARMED_WORKFLOW_STATE)
  }

  function armCommand(
    template: WorkflowTemplate,
    inputs: Record<string, string | null>,
    audienceName?: string | null,
  ) {
    persist({
      slug: template.slug,
      template,
      inputs,
      intakeDirection: null,
      audienceName: audienceName ?? null,
      latchedStartKey: null,
    })
  }

  function setIntakeDirection(direction: string | null) {
    const current = stateRef.current
    persist({
      ...current,
      intakeDirection: direction,
      latchedStartKey: direction == null ? null : current.latchedStartKey,
    })
  }

  const latchIntakeStart = useCallback((key: string) => {
    persist({ ...stateRef.current, latchedStartKey: key })
  }, [threadToken])

  function dismissDrop() {
    if (threadToken) {
      clearArmedWorkflowDropFlag(threadToken)
    }
    setWasDropped(false)
  }

  function rememberAudience(audience: RememberedAudience) {
    setRememberedAudience(audience)
    writeRememberedAudience(orgId, audience)
  }

  function forgetRememberedAudience() {
    setRememberedAudience(null)
    clearRememberedAudienceStorage(orgId)
  }

  return {
    slug: state.slug,
    template: state.template,
    inputs: state.inputs,
    intakeDirection: state.intakeDirection,
    audienceName: state.audienceName,
    latchedStartKey: state.latchedStartKey,
    proceedWithoutStimulus: state.proceedWithoutStimulus ?? false,
    wasDropped,
    rememberedAudience,
    armSlug,
    clear,
    armCommand,
    setIntakeDirection,
    latchIntakeStart,
    dismissDrop,
    rememberAudience,
    forgetRememberedAudience,
  }
}
